export type AIContentType =
  | 'publicacion'
  | 'concurso'
  | 'evento'
  | 'promocion'
  | 'patrocinio'
  | 'mensaje';

export interface AIContentTypeDef {
  id: AIContentType;
  label: string;
  icon: string;
  color: string;
  bg: string;
  description: string;
  fields: { id: string; label: string; placeholder: string; type: 'text' | 'textarea' | 'select'; options?: string[] }[];
  defaultRadio: string;
  defaultTone: string;
}

export interface AIWizardHistoryEntry {
  id: string;
  type: AIContentType;
  typeLabel: string;
  radioName: string;
  inputs: Record<string, string>;
  result: string;
  timestamp: string;
}

export const contentTypeDefs: AIContentTypeDef[] = [
  {
    id: 'publicacion',
    label: 'Publicación',
    icon: 'ri-article-line',
    color: 'text-sky-600',
    bg: 'bg-sky-50',
    description: 'Generá posts atractivos para el feed de tu radio. Ideal para novedades, lanzamientos y engagement.',
    fields: [
      { id: 'radio', label: 'Radio', placeholder: 'Ej: Radio Élite Digital', type: 'text' },
      { id: 'tema', label: 'Tema', placeholder: '¿De qué trata la publicación?', type: 'text' },
      { id: 'tono', label: 'Tono', placeholder: 'Elegí el estilo', type: 'select', options: ['Profesional', 'Cercano', 'Enérgico', 'Divertido', 'Inspirador'] },
      { id: 'detalle', label: 'Detalles adicionales', placeholder: 'Mencioná horarios, invitados, enlaces...', type: 'textarea' },
    ],
    defaultRadio: 'Radio Élite Digital',
    defaultTone: 'Enérgico',
  },
  {
    id: 'concurso',
    label: 'Concurso',
    icon: 'ri-trophy-line',
    color: 'text-amber-600',
    bg: 'bg-amber-50',
    description: 'Creá concursos que disparen la participación. Definí premios, reglas y mecánicas en segundos.',
    fields: [
      { id: 'radio', label: 'Radio', placeholder: 'Ej: Éxitos FM', type: 'text' },
      { id: 'nombre', label: 'Nombre del concurso', placeholder: 'Ej: La Mañana Ganadora', type: 'text' },
      { id: 'premio', label: 'Premio', placeholder: '¿Qué ganan los participantes?', type: 'text' },
      { id: 'mecanica', label: 'Mecánica', placeholder: '¿Cómo se participa?', type: 'textarea' },
    ],
    defaultRadio: 'Éxitos FM',
    defaultTone: 'Enérgico',
  },
  {
    id: 'evento',
    label: 'Evento',
    icon: 'ri-calendar-event-line',
    color: 'text-violet-600',
    bg: 'bg-violet-50',
    description: 'Anunciá eventos en vivo, transmisiones especiales o encuentros con la comunidad.',
    fields: [
      { id: 'radio', label: 'Radio', placeholder: 'Ej: Deportiva Total', type: 'text' },
      { id: 'nombre', label: 'Nombre del evento', placeholder: 'Ej: Noche de Campeones', type: 'text' },
      { id: 'fecha', label: 'Fecha y hora', placeholder: 'Ej: Sábado 22 de junio, 20:00', type: 'text' },
      { id: 'descripcion', label: 'Descripción', placeholder: '¿Qué va a pasar en el evento?', type: 'textarea' },
    ],
    defaultRadio: 'Deportiva Total',
    defaultTone: 'Enérgico',
  },
  {
    id: 'promocion',
    label: 'Promoción',
    icon: 'ri-flashlight-line',
    color: 'text-emerald-600',
    bg: 'bg-emerald-50',
    description: 'Lanzá promociones y ofertas para atraer patrocinadores o fidelizar oyentes.',
    fields: [
      { id: 'radio', label: 'Radio', placeholder: 'Ej: Stereo Visión', type: 'text' },
      { id: 'producto', label: 'Producto o servicio', placeholder: '¿Qué estás promocionando?', type: 'text' },
      { id: 'beneficio', label: 'Beneficio', placeholder: 'Ej: 30% de descuento, 2x1, mes gratis...', type: 'text' },
      { id: 'vigencia', label: 'Vigencia', placeholder: 'Ej: Del 15 al 30 de junio', type: 'text' },
    ],
    defaultRadio: 'Stereo Visión',
    defaultTone: 'Profesional',
  },
  {
    id: 'patrocinio',
    label: 'Patrocinio',
    icon: 'ri-hand-heart-line',
    color: 'text-rose-600',
    bg: 'bg-rose-50',
    description: 'Generá propuestas de patrocinio profesionales para captar marcas y sponsors.',
    fields: [
      { id: 'radio', label: 'Radio', placeholder: 'Ej: Radio Nueva Actitud', type: 'text' },
      { id: 'marca', label: 'Marca objetivo', placeholder: '¿A qué marca querés proponerle?', type: 'text' },
      { id: 'audiencia', label: 'Audiencia', placeholder: 'Ej: 50K oyentes, 18-35 años, CDMX', type: 'text' },
      { id: 'propuesta', label: 'Propuesta de valor', placeholder: '¿Qué le ofrecés a la marca?', type: 'textarea' },
    ],
    defaultRadio: 'Radio Nueva Actitud',
    defaultTone: 'Profesional',
  },
  {
    id: 'mensaje',
    label: 'Mensaje',
    icon: 'ri-chat-smile-2-line',
    color: 'text-indigo-600',
    bg: 'bg-indigo-50',
    description: 'Redactá mensajes para tu audiencia: saludos al aire, respuestas en cabina o anuncios rápidos.',
    fields: [
      { id: 'radio', label: 'Radio', placeholder: 'Ej: Radio Sol 95.1', type: 'text' },
      { id: 'tipo', label: 'Tipo de mensaje', placeholder: 'Elegí el tipo', type: 'select', options: ['Saludo al aire', 'Anuncio de programa', 'Agradecimiento', 'Invitación a participar', 'Mensaje motivacional'] },
      { id: 'contexto', label: 'Contexto', placeholder: '¿En qué momento o programa se usará?', type: 'text' },
    ],
    defaultRadio: 'Radio Sol 95.1',
    defaultTone: 'Cercano',
  },
];

export function generateAIContent(type: AIContentType, inputs: Record<string, string>): string {
  const radio = inputs.radio || contentTypeDefs.find((d) => d.id === type)?.defaultRadio || 'Tu Radio';

  switch (type) {
    case 'publicacion':
      return `📻 ¡${radio} tiene novedades!\n\n${inputs.tema || 'Imperdible programación esta semana'} con lo mejor de la música y el entretenimiento.\n\nEsta semana te traemos:\n✨ Contenido exclusivo que no vas a encontrar en otro lado\n🎙️ Invitados especiales que van a dar que hablar\n🎧 La mejor música seleccionada por nuestros DJs\n\n${inputs.detalle ? `📌 ${inputs.detalle}\n\n` : ''}Prendete a ${radio} y viví la experiencia. ¿Ya nos seguís? Activa las notificaciones para no perderte nada.\n\n#RadioEnVivo #${radio.replace(/\s+/g, '')} #MúsicaReal`;

    case 'concurso':
      return `🎉 ¡CONCURSO EN ${radio.toUpperCase()}! 🎉\n\nTe presentamos "${inputs.nombre || 'El Gran Sorteo'}"\n\n🏆 PREMIO: ${inputs.premio || 'Un premio increíble que no te podés perder'}\n\n¿CÓMO PARTICIPAR?\n${inputs.mecanica || '1. Seguinos en todas nuestras redes\n2. Compartí esta publicación\n3. Etiquetá a 3 amigos en los comentarios\n4. ¡Listo! Ya estás participando'}\n\n📅 El ganador se anuncia en vivo el próximo viernes a las 18:00.\n\n⚠️ Bases y condiciones disponibles en nuestra web.\n\n¡Mucha suerte a todos! 🍀\n\n#Concurso${radio.replace(/\s+/g, '')} #GanáCon${radio.split(' ').join('')}`;

    case 'evento':
      return `📅 ¡EVENTO ESPECIAL EN ${radio.toUpperCase()}!\n\nTe invitamos a "${inputs.nombre || 'Una Noche Inolvidable'}"\n\n🗓️ ${inputs.fecha || 'Próximo sábado a las 20:00'}\n📍 Transmisión en vivo por ${radio}\n\n${inputs.descripcion || 'Una noche única con los mejores momentos musicales, invitados sorpresa y la mejor compañía. Preparate para vivir una experiencia que solo nuestra radio te puede ofrecer.'}\n\n🎟️ Entrada libre y gratuita. Solo necesitás sintonizarnos.\n\nCompartí este evento con quien no se lo puede perder 👇\n\n#EventoEnVivo #${radio.replace(/\s+/g, '')} #MúsicaYComunidad`;

    case 'promocion':
      return `🔥 ¡PROMOCIÓN EXCLUSIVA! 🔥\n\n${radio} te trae una oferta que no vas a querer dejar pasar.\n\n🎯 ${inputs.producto || 'Nuestro producto estrella'}\n💎 ${inputs.beneficio || 'Beneficio exclusivo para oyentes'}\n⏰ ${inputs.vigencia || 'Por tiempo limitado'}\n\nEsta promoción es exclusiva para nuestra comunidad de oyentes. Aprovechá antes de que se acabe.\n\n¿Cómo acceder?\n1. Mencioná esta promo al contactarnos\n2. Decinos que escuchaste la promo en ${radio}\n3. ¡Disfrutá tu beneficio!\n\nCompartilo con alguien que lo necesita 🙌\n\n#Promo${radio.replace(/\s+/g, '')} #Exclusivo #OfertaEspecial`;

    case 'patrocinio':
      return `📊 PROPUESTA DE PATROCINIO — ${radio.toUpperCase()}\n\nEstimado equipo de ${inputs.marca || '[Nombre de la Marca]'},\n\nMe dirijo a ustedes para presentarles una oportunidad de patrocinio en ${radio}, una de las emisoras con mayor crecimiento en la plataforma RadioFreeWeb.\n\n🎯 NUESTRA AUDIENCIA:\n${inputs.audiencia || '• Audiencia fiel y en crecimiento\n• Alto engagement en redes sociales\n• Comunidad activa y participativa'}\n\n💡 PROPUESTA DE VALOR:\n${inputs.propuesta || '• Menciones en vivo durante la programación\n• Banner destacado en nuestra página\n• Publicaciones en redes sociales\n• Participación en eventos y transmisiones especiales'}\n\n📈 ¿POR QUÉ ${radio.toUpperCase()}?\n• Contenido de calidad con crecimiento constante\n• Audiencia segmentada y comprometida\n• Flexibilidad para adaptarnos a los objetivos de tu marca\n\nMe encantaría agendar una reunión para explorar cómo podemos colaborar juntos. ¿Qué día te queda cómodo esta semana?\n\nSaludos cordiales,\nEquipo de ${radio}`;

    case 'mensaje':
      return `🎙️ ¡${inputs.tipo || 'MENSAJE'} DE ${radio.toUpperCase()}!\n\n${inputs.tipo === 'Saludo al aire' ? `Queremos mandarle un saludo enorme a toda nuestra audiencia que nos está escuchando en este momento. ${inputs.contexto || 'Gracias por acompañarnos una vez más en esta hermosa jornada.'} ¡Son el corazón de ${radio}!\n\nRecuerden que pueden mandarnos sus saludos y los leemos al aire. ❤️` : inputs.tipo === 'Anuncio de programa' ? `No te pierdas nuestro próximo programa ${inputs.contexto || 'que viene con todo'}.\n\nEsta semana preparamos contenido increíble que no te podés perder. Invitados, música, sorteos y mucho más.\n\n📻 Sintonizanos y activá las notificaciones.` : inputs.tipo === 'Agradecimiento' ? `Queremos agradecer de corazón a cada uno de ustedes por hacer de ${radio} lo que es hoy. ${inputs.contexto || 'Cada mensaje, cada like, cada vez que nos sintonizan nos llena de energía para seguir.'}\n\n¡Gracias totales! 🙏` : inputs.tipo === 'Invitación a participar' ? `¡Te invitamos a ser parte! ${inputs.contexto || 'Queremos escuchar tu voz en nuestro próximo programa.'}\n\nMandanos tu mensaje, tu canción favorita o simplemente saludanos. ¡Queremos saber de vos!\n\n📲 Escribinos por el chat de la app o redes sociales.` : `${inputs.contexto || 'Hoy es un gran día para hacer lo que te gusta. No te rindas, seguí adelante y recordá que siempre hay alguien del otro lado del dial acompañándote.'}\n\nCon cariño, el equipo de ${radio} 💫`}`;
    default:
      return '';
  }
}

export function simulateGeneration(delayMs: number = 1800): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, delayMs + Math.random() * 800);
  });
}